import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useAgencyProfessionalsQuery } from '../../../redux/external-agency';
import { PRIVATE_PATH } from '../../../constants';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import DatatableActionColumn from './DatatableActionColumn';

import '../../../components/professionals/ProfessionalsList.scss';

const mapCaregiversData = (data) =>
	data?.map((caregiver) => {
		const {
			id,
			name,
			status,
			has_joined: hasJoined,
			is_multi_agency: isMultiAgency,
			is_multi_role: isMultiRole,
			specialties,
			certifications,
			state_code: stateCode,
			city_name: cityName,
			enabled_for_agency: enabledForAgency
		} = caregiver;

		 
		const licenses = (certifications?.map((certif) => certif?.resource_type) || []).join(', ') || '-';
		const mappedSpecialties = (
			specialties?.map((specialty) => `${specialty.code.split('_', 1)} - ${specialty.label}`) || []
		).sort((a, b) => a.localeCompare(b));

		return {
			id,
			name,
			status,
			hasJoined,
			isMultiAgency,
			isMultiRole,
			licenses,
			specialties: mappedSpecialties,
			specialtiesString: mappedSpecialties.length ? mappedSpecialties.join(', ') : '-',
			state: stateCode,
			city: cityName,
			enabledForAgency
		};
	});

const isCgDisabled = (caregiver) => !caregiver.hasJoined && (caregiver.isMultiAgency || caregiver.isMultiRole);
const isCgDeactivated = (caregiver) => caregiver.status === 'deactivated' || caregiver.status === 'disabled';

const ToolbarItems = ({handleCreateProfessional}) => (
	<Button className="btn-add-professional" onClick={handleCreateProfessional}>
		+ Add professional
	</Button>
);

const AgencyProfessionalsList = () => {
	const history = useHistory();

	const { data: caregivers, isLoading } = useAgencyProfessionalsQuery(undefined, {
		refetchOnMountOrArgChange: true
	});

	const handleCreateProfessional = () => history.push(`${PRIVATE_PATH.PROFESSIONALS_PAGE}/create`);

	const professionals = caregivers ? mapCaregiversData(caregivers) : [];

	const [filteredProfessionalsCount, setFilteredProfessionalsCount] = useState(null);

	const statusTemplate = useCallback((item) => {
		const isDisabled = isCgDeactivated(item);
		let cellClasses = isDisabled ? 'column-status disabled' : 'column-status';
		if (item.status === 'pending' || item.status.includes('app invite sent')) {
			cellClasses += ' pending-column-status';
		} else if (item.status === 'terminated') {
			cellClasses += ' status-column-terminated';
		}
		return <span className={cellClasses}>{item.status}</span>;
	}, []);

	const specialtyTemplate = useCallback((item) => {
		const hasSpecialties = !!item.specialties.length;
		const isDeactivated = isCgDeactivated(item);
		return (
			<span
				style={{
					textDecoration: hasSpecialties && isDeactivated ? 'line-through' : 'none'
				}}
			>
				{item.specialtiesString}
			</span>
		);
	}, []);

	const professionalTemplate = (caregiver) => <DatatableActionColumn caregiver={caregiver} />;

	const defaultTemplate = useCallback((item, columnData) => {
		const { field } = columnData;
		return item[field] ? item[field] : '-';
	}, []);

	const rowClass = useCallback((item) => {
		return {
			'row-disabled': isCgDeactivated(item)
		};
	}, []);

	const cellClass = useCallback((item, columnData) => {
		const data = columnData.props.value[columnData.rowIndex] || {};
		// the rowIndex above gets wrong data when search & filter
		const disabledCheck = isCgDisabled(data);
		return {
			'table-cell': true,
			disabled: columnData.field === 'name' && disabledCheck,
			'no-line-through': ['status', 'actions'].includes(columnData.field)
		};
	}, []);

	const onFilter = (e) => setFilteredProfessionalsCount(e.length);
	const _filteredProfessionalsCount =
		filteredProfessionalsCount !== null ? filteredProfessionalsCount : professionals.length;

	return (
		<div className="professionals-list">
			<div className="toolbar">
				<p className="total-count">
					{_filteredProfessionalsCount} Professional{_filteredProfessionalsCount > 1 ? 's' : ''}
				</p>
				<div className="flex-one" />
				<ToolbarItems handleCreateProfessional={handleCreateProfessional}/>
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={professionals}
						scrollable
						scrollHeight="flex"
						removableSort
						sortField="name"
						dataKey="id"
						virtualScrollerOptions={{ itemSize: 65 }}
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName={cellClass}
						rowClassName={rowClass}
						loading={isLoading}
						emptyMessage="No results found"
						loadingIcon={<LoadingSpinner />}
						onValueChange={onFilter}
					>
						<Column field="name" header="Professional" style={{ width: '15%' }} sortable body={defaultTemplate} />
						<Column field="status" header="Status" style={{ width: '8%' }} sortable body={statusTemplate} />
						<Column field="licenses" header="License type" style={{ width: '8%' }} sortable />
						<Column field="specialties" header="Specialty" style={{ width: '17%' }} sortable body={specialtyTemplate} />
						<Column field="city" header="City" style={{ width: '15%' }} sortable body={defaultTemplate} />
						<Column field="state" header="State" style={{ width: '4%' }} sortable body={defaultTemplate} />
						<Column
							field="actions"
							header="Actions"
							style={{ width: '4%' }}
							alignFrozen="right"
							exportable={false}
							body={professionalTemplate}
						/>
					</DataTable>
				</div>
			</div>
		</div>
	);
};

export default AgencyProfessionalsList;
